import { useEffect, useRef, useState } from 'react'

import ThemeModule, { DEFAULT_ACCENT_COLOR } from '../../modules/theme/Module'
import ModalModule from '../../modules/modal/Module'

import { IModalProps } from '../../modules/modal/types'
import { IWidgetStyle } from '../../modules/theme/types'
import { ISessionUseSyncTheme } from '../types'

const useSyncTheme: ISessionUseSyncTheme = (currentActionState, customStyleState, rootRef) => {
  const action = currentActionState.currentActionRef.current
  const customStyle = customStyleState.customStyleRef.current

  const lastActionStyleRef = useRef<IWidgetStyle>(null)
  if (action?.data?.style && Object.keys(action.data.style).length > 0) {
    lastActionStyleRef.current = action.data.style
  }

  const [customAccentColorCSS, setCustomAccentColorCSS] = useState<string>('')
  const customAccentColorCSSRef = useRef<string>()
  customAccentColorCSSRef.current = customAccentColorCSS
  const lastAccentColorRef = useRef<string>(null)

  const customAccentColorCSSClassNameRef = useRef<string>(null)
  if (null == customAccentColorCSSClassNameRef.current) {
    customAccentColorCSSClassNameRef.current = `SW-Accent-Color-${Math.round(Math.random() * 1000000).toString(
      36
    )}${Date.now().toString(36)}`
  }

  useEffect(() => {
    const style = customStyle || lastActionStyleRef.current
    if (style) {
      ThemeModule.updateStyle(style)
    }
    // apply custom accent color
    if (lastAccentColorRef.current != style?.accentColor) {
      if (!style?.accentColor || style.accentColor.toUpperCase() === DEFAULT_ACCENT_COLOR.toUpperCase()) {
        if (customAccentColorCSSRef.current) {
          setCustomAccentColorCSS('')
        }
      } else {
        const accentA_HSL = ThemeModule.rgbHex2hsl(style.accentColor)
        const accentB_HSL = ThemeModule.accentAtoB(accentA_HSL)
        const accentA_HSL_string = `${accentA_HSL[0]}, ${accentA_HSL[1]}%, ${accentA_HSL[2]}%`
        const accentB_HSL_string = `${accentB_HSL[0]}, ${accentB_HSL[1]}%, ${accentB_HSL[2]}%`
        if (
          !customAccentColorCSSRef.current ||
          !customAccentColorCSSRef.current.includes(`--prime-custom-accent-a-color-hsl: ${accentA_HSL_string};`) ||
          !customAccentColorCSSRef.current.includes(`--prime-custom-accent-b-color-hsl: ${accentB_HSL_string};`)
        ) {
          setCustomAccentColorCSS(
            `.${customAccentColorCSSClassNameRef.current} {accent-color:${style.accentColor};--prime-custom-accent-a-color-hsl:${accentA_HSL_string};--prime-custom-accent-b-color-hsl:${accentB_HSL_string};}`
          )
        }
      }
      lastAccentColorRef.current = style.accentColor
    }
  }, [action, customStyle])

  useEffect(() => {
    const style = customStyle || lastActionStyleRef.current
    if (rootRef.current && style && Object.keys(style).length > 0) {
      const modalID = ModalModule.findModalID(rootRef.current)
      if (modalID) {
        const background: IModalProps['background'] = {}
        if (null != style.backgroundColor) {
          background.color = style.backgroundColor
        }
        if (null != style.backgroundImage) {
          background.image =
            style.backgroundImage.startsWith('/') || style.backgroundImage.startsWith('http')
              ? `url(${style.backgroundImage})`
              : style.backgroundImage
        } else {
          background.image = 'none'
        }
        if (null != style.backgroundFit) {
          background.fit = style.backgroundFit
        }
        if (null != style.backgroundTile) {
          background.tile = style.backgroundTile
        }
        ModalModule.config({
          [modalID]: {
            background
          }
        })
      }
    }
  }, [action, rootRef, customStyle])

  return { customAccentColorCSSRef, customAccentColorCSSClassNameRef } as const
}

export default useSyncTheme
